import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Pill, Spinner, TextInput } from '../../components/ui';

// 阶段 3：返工交货（客户受托件，不扣公司成品库存）。
// 2026-10 生产化收口：页面只保留中性帮助提示（返修不含费用，增项另开销售合同），不建任何费用登记/开票入口。
// 设计文档 docs/aftersales-rework-batch-redesign-2026-10-16.md v3 §6。

type DeliverableItem = {
  id: string;
  status?: string | null;
  customer_serial?: string | null;
  internal_identifier?: string | null;
  item_code?: string | null;
  item_name_zh?: string | null;
  released_inspection_id?: string | null;
};
type Delivery = { id: string; number: string; status: string; delivery_date?: string | null; line_count?: number };

export function ReworkDeliveryPanel({ batchId, canWrite, onChanged }: { batchId: string; canWrite: boolean; onChanged: () => void }) {
  const base = `/api/rework-batches/${encodeURIComponent(batchId)}`;
  const [deliverable, setDeliverable] = useState<DeliverableItem[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState({ carrier: '', tracking_number: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    Promise.all([
      api.detail<{ items: DeliverableItem[] }>(`${base}/deliverable`),
      api.list<Delivery>(`${base}/deliveries`),
    ])
      .then(([d, dl]) => { setDeliverable(d.items || []); setDeliveries(dl); })
      .catch((e: any) => setErr(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [base]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true); setErr(''); setMsg('');
    try { await fn(); setMsg(ok); load(); onChanged(); }
    catch (e: any) { setErr(e?.message || '操作失败'); }
    finally { setBusy(false); }
  };

  const pickedIds = deliverable.filter((i) => picked[i.id]).map((i) => ({ item_id: i.id }));

  const createDelivery = (status: 'draft' | 'confirmed') => run(
    () => api.post(`${base}/deliveries`, { items: pickedIds, status, carrier: form.carrier.trim() || undefined, tracking_number: form.tracking_number.trim() || undefined }),
    status === 'draft' ? '交货草稿已创建（工件已预占）' : '返工交货已确认（不扣成品库存）',
  ).then(() => { setPicked({}); setForm({ carrier: '', tracking_number: '' }); });

  const confirmDelivery = (id: string) => run(
    () => api.post(`/api/rework-batches/deliveries/${encodeURIComponent(id)}/confirm`, {}),
    '交货已确认',
  );

  if (loading) return <Spinner label="正在读取交货…" />;

  return (
    <div style={{ marginTop: 10 }}>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      <div className="strong" style={{ marginBottom: 4 }}>返工交货（客户受托件 · 不扣公司成品库存、不增原合同已交数量）</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
        返修不含费用，如产生增项请另开销售合同。
      </div>
      <table className="grid">
        <thead><tr><th style={{ width: 40 }}></th><th>工件</th><th>物料</th><th>状态</th><th>放行质检</th></tr></thead>
        <tbody>
          {deliverable.length === 0 ? <tr><td colSpan={5} className="muted">暂无可交货工件（须已检验放行合格）</td></tr> : null}
          {deliverable.map((i) => (
            <tr key={i.id}>
              <td><input type="checkbox" checked={!!picked[i.id]} onChange={(e) => setPicked((s) => ({ ...s, [i.id]: e.target.checked }))} /></td>
              <td className="muted">{i.customer_serial || i.internal_identifier || '—'}</td>
              <td className="muted">{i.item_name_zh || i.item_code || '—'}</td>
              <td className="muted">{i.status || '—'}</td>
              <td className="muted">{i.released_inspection_id ? '已放行' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite && deliverable.length > 0 ? (
        <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ width: 160 }}><TextInput placeholder="承运商" value={form.carrier} onChange={(e) => setForm((s) => ({ ...s, carrier: e.target.value }))} /></div>
          <div style={{ width: 180 }}><TextInput placeholder="运单号" value={form.tracking_number} onChange={(e) => setForm((s) => ({ ...s, tracking_number: e.target.value }))} /></div>
          <button className="btn" disabled={busy || pickedIds.length === 0} onClick={() => createDelivery('draft')}>保存草稿（预占）</button>
          <button className="btn primary" disabled={busy || pickedIds.length === 0} onClick={() => createDelivery('confirmed')}>确认交货</button>
        </div>
      ) : null}

      <table className="grid" style={{ marginTop: 6 }}>
        <thead><tr><th>交货单号</th><th>日期</th><th>件数</th><th>状态</th><th></th></tr></thead>
        <tbody>
          {deliveries.length === 0 ? <tr><td colSpan={5} className="muted">暂无返工交货单</td></tr> : null}
          {deliveries.map((d) => (
            <tr key={d.id}>
              <td>{d.number}</td>
              <td className="muted">{d.delivery_date || '—'}</td>
              <td className="muted">{d.line_count ?? '—'}</td>
              <td><Pill tone={d.status === 'confirmed' || d.status === 'delivered' ? 'ok' : 'warn'} label={d.status} /></td>
              <td>
                {canWrite && d.status === 'draft' ? (
                  <button className="btn" disabled={busy} onClick={() => confirmDelivery(d.id)}>确认</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
